import Phaser from "phaser";

const DOT_TEXTURE = "fxDot";

/** A soft round dot drawn once at runtime; tinted per effect. */
export function ensureEffectTextures(scene: Phaser.Scene): void {
	if (scene.textures.exists(DOT_TEXTURE)) return;
	const size = 32;
	const canvas = scene.textures.createCanvas(DOT_TEXTURE, size, size);
	if (!canvas) return;
	const ctx = canvas.getContext();
	const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
	g.addColorStop(0, "rgba(255,255,255,1)");
	g.addColorStop(0.35, "rgba(255,255,255,0.8)");
	g.addColorStop(1, "rgba(255,255,255,0)");
	ctx.fillStyle = g;
	ctx.fillRect(0, 0, size, size);
	canvas.refresh();
}

/**
 * Particle effects for one viewport, layered over the original sprites: engine exhaust,
 * explosion sparks and hit sparks. Emitters live in *continuous* world coordinates (the
 * owner moves them with `follow()` so that particles stay put in the world as the camera
 * moves, including across the wrap seam).
 */
export class ParticleFx {
	private readonly exhaust: Phaser.GameObjects.Particles.ParticleEmitter;
	private readonly sparks: Phaser.GameObjects.Particles.ParticleEmitter;
	private readonly dust: Phaser.GameObjects.Particles.ParticleEmitter;
	/** Emission direction (degrees) for the next exhaust particle. */
	private exhaustAngle = 0;

	constructor(scene: Phaser.Scene, layer: Phaser.GameObjects.Layer, depth: number) {
		ensureEffectTextures(scene);
		const make = (config: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig) => {
			const emitter = scene.make.particles({ key: DOT_TEXTURE, config }, false);
			emitter.setDepth(depth).setBlendMode(Phaser.BlendModes.ADD);
			layer.add(emitter);
			return emitter;
		};

		this.exhaust = make({
			emitting: false,
			lifespan: { min: 180, max: 320 },
			speed: { min: 60, max: 140 },
			angle: { onEmit: () => this.exhaustAngle + Phaser.Math.Between(-12, 12) },
			scale: { start: 0.5, end: 0 },
			alpha: { start: 0.8, end: 0 },
			color: [0xfff3b0, 0xffb347, 0xff5a2a],
		});
		this.sparks = make({
			emitting: false,
			lifespan: { min: 350, max: 750 },
			speed: { min: 40, max: 260 },
			angle: { min: 0, max: 360 },
			scale: { start: 0.35, end: 0 },
			alpha: { start: 1, end: 0 },
			color: [0xffffff, 0xffd166, 0xff6b35, 0x8a1c1c],
		});
		this.dust = make({
			emitting: false,
			lifespan: { min: 400, max: 900 },
			speed: { min: 20, max: 120 },
			angle: { min: 0, max: 360 },
			scale: { start: 0.7, end: 0.1 },
			alpha: { start: 0.6, end: 0 },
			color: [0xd9c3a0, 0x8c7456],
		});
	}

	/** Keeps particle coordinates = continuous world coordinates relative to the camera. */
	follow(cameraWorldX: number, cameraWorldY: number): void {
		for (const e of [this.exhaust, this.sparks, this.dust])
			e.setPosition(-cameraWorldX, -cameraWorldY);
	}

	/** Two engine plumes behind a thrusting ship (world position, heading in radians). */
	/** Engine exhaust; `count` > 1 for a stronger flame (warp speed). */
	thrust(x: number, y: number, angle: number, radius: number, count = 1): void {
		const back = angle + Math.PI;
		this.exhaustAngle = Phaser.Math.RadToDeg(back);
		const bx = x + Math.cos(back) * radius * 0.8;
		const by = y + Math.sin(back) * radius * 0.8;
		const px = -Math.sin(angle) * radius * 0.45;
		const py = Math.cos(angle) * radius * 0.45;
		this.exhaust.emitParticleAt(bx + px, by + py, count);
		this.exhaust.emitParticleAt(bx - px, by - py, count);
	}

	explosion(x: number, y: number, big: boolean): void {
		this.sparks.emitParticleAt(x, y, big ? 36 : 14);
		if (!big) this.dust.emitParticleAt(x, y, 10);
	}

	hit(x: number, y: number): void {
		this.sparks.emitParticleAt(x, y, 6);
	}

	destroy(): void {
		this.exhaust.destroy();
		this.sparks.destroy();
		this.dust.destroy();
	}
}
